import type { MouseEvent } from 'react'
import { useState } from 'react'
import startCase from 'lodash/startCase'
import { Copy, ExternalLink, Globe, LoaderCircleIcon, Search } from 'lucide-react'
import { LinkBase, useGetList, useNotify } from 'ra-core'
import { DateField } from '@internal/ui-kit/components/admin'
import { Avatar, AvatarFallback, AvatarImage } from '@internal/ui-kit/components/ui/avatar'
import { Badge } from '@internal/ui-kit/components/ui/badge'
import { Button, buttonVariants } from '@internal/ui-kit/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@internal/ui-kit/components/ui/card'
import { Popover, PopoverContent, PopoverTrigger } from '@internal/ui-kit/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@internal/ui-kit/components/ui/tooltip'
import { fromApiUtc } from '@internal/ui-kit/lib/fromApiUtc'
import { optimizeImageUrl } from '@internal/ui-kit/lib/optimizeImageUrl'
import { cn } from '@internal/ui-kit/lib/utils'

/**
 * The loosely-typed shape this card needs off either call site's own record:
 * the full `users` resource record (`apps/users/schema.ts`'s `userSchema`),
 * or an order's embedded `owner` (`apps/orders/schema.ts` models it as
 * `z.unknown()` since the API expands a full `User` inline with no reference
 * resource to resolve it through).
 */
export type CustomerCardPerson = {
  user_id?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone_number?: string | null
  avatar?: { url?: string | null } | null
  scopes?: string[] | null
  company_name?: string | null
  login_type?: string | null
  domain?: string | null
  created_at?: string | null
  signed_up_at?: string | null
  sex?: string | null
  date_of_birth?: string | null
}

/** Same copy-to-clipboard affordance `apps/orders`'/`apps/audit-log`'s own `CopyIconButton` use (HATCH-36 review round 2) — email/phone are too easy to mistype by hand. */
function CopyIconButton({ value, label }: { value: string; label: string }) {
  const notify = useNotify()
  const handleCopy = async (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    await navigator.clipboard.writeText(value)
    notify(`${label} copied`, { type: 'info' })
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-muted-foreground hover:text-foreground shrink-0"
      title={`Copy ${label.toLowerCase()}`}
    >
      <Copy className="size-3.5" />
    </button>
  )
}

function initialsFor(name: string, email: string | null | undefined): string {
  if (name) return name.slice(0, 2).toUpperCase()
  if (email) return email.slice(0, 2).toUpperCase()
  return '?'
}

type ContactMatch = {
  id: string | number
  user_id?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone_number?: string | null
  scopes?: string[] | null
}

/** One row of the Lookup dropdown's results — name, copyable phone number, scope badges, and a link to that user's own profile. */
function ContactMatchRow({ contact }: { contact: ContactMatch }) {
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ')
  const userId = contact.user_id ?? contact.id
  return (
    <div className="flex items-start justify-between gap-2 py-1.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{name || '—'}</span>
        {contact.phone_number ? (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <span className="truncate">{contact.phone_number}</span>
            <CopyIconButton value={contact.phone_number} label="Phone number" />
          </span>
        ) : null}
        {contact.scopes?.length ? (
          <div className="flex flex-wrap items-center gap-1">
            {contact.scopes.map((scope) => (
              <Badge key={scope} variant="outline">
                {scope}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <LinkBase
            to={`/users/${userId}/profile`}
            className={buttonVariants({ variant: 'ghost', size: 'icon', className: 'size-6 shrink-0' })}
          >
            <ExternalLink className="size-3.5" />
          </LinkBase>
        </TooltipTrigger>
        <TooltipContent>Open full profile</TooltipContent>
      </Tooltip>
    </div>
  )
}

/**
 * The card's bottom-right "Lookup" button (only shown when the customer has
 * an email — anon users might not) — looks up other `users` records sharing
 * that same email (`GET /users?search=`, same free-text param `apps/users`'
 * own filter and `apps/orders`' owner lookup already use, `dataProvider.ts`'s
 * `users` resource). These are almost always the same person's own other
 * sessions/devices (ticket feedback round 1: "most probably other sessions
 * in different browsers / devices") — an anon session's order gets
 * re-assigned to the account that later logs in with that email, but the
 * anon record itself isn't merged, so a phone number entered as a guest can
 * be stranded on it.
 */
function ContactsLookupButton({ email, currentUserId }: { email: string; currentUserId?: string | null }) {
  const [open, setOpen] = useState(false)
  const { data, isPending } = useGetList<ContactMatch>(
    'users',
    { pagination: { page: 1, perPage: 25 }, filter: { q: email } },
    { enabled: open },
  )
  const matches = (data ?? []).filter(
    (contact) => contact.email === email && String(contact.user_id ?? contact.id) !== String(currentUserId),
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="size-7" title="Lookup contacts with this email">
          <Search className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <p className="text-muted-foreground mb-2 text-xs font-medium">Contacts with same email:</p>
        {isPending ? (
          <div className="flex items-center justify-center py-4">
            <LoaderCircleIcon className="text-muted-foreground size-4 animate-spin" />
          </div>
        ) : matches.length === 0 ? (
          <p className="text-muted-foreground text-sm">No other contacts found.</p>
        ) : (
          <div className="flex max-h-72 flex-col divide-y overflow-y-auto">
            {matches.map((contact) => (
              <ContactMatchRow key={String(contact.user_id ?? contact.id)} contact={contact} />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * The shared "who is this person" card (HATCH-274) — avatar, first/last
 * name, email, phone, scope badges, company name (if provided), login type
 * badge, domain, gender/date of birth (if set, HATCH-401), and the account's
 * signup/creation timestamp. Used both on
 * the order detail page (the order's embedded `owner`) and the user profile
 * page's readonly Profile tab — this card never edits; the profile page's
 * existing `EditButton`/`UserEditPage` flow stays the only way to change any
 * of these fields.
 *
 * `linkToProfile` adds an "open full profile" link (hidden by default when
 * the card is already rendered on that same profile page).
 */
export function CustomerCard({
  customer,
  className,
  linkToProfile = true,
}: {
  customer: CustomerCardPerson | null | undefined
  className?: string
  linkToProfile?: boolean
}) {
  if (!customer) return null
  const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(' ')
  const scopes = customer.scopes ?? []
  // Ticket: "Created at - for anon / signed_up_at - for email_verified" — an
  // anonymous account has no `email_verified` scope yet, so it only has a
  // `created_at`; a verified account's meaningful date is when it signed up.
  const isEmailVerified = scopes.includes('email_verified')
  const signupDate = isEmailVerified ? customer.signed_up_at : customer.created_at
  const signupLabel = isEmailVerified ? 'Signed up' : 'Created'

  return (
    <Card className={cn('w-full max-w-xs gap-3 py-4', className)}>
      <CardHeader className="flex items-center gap-2 px-4">
        <Avatar className="size-9">
          <AvatarImage src={optimizeImageUrl(customer.avatar?.url, 72)} alt={fullName || customer.email || ''} />
          <AvatarFallback>{initialsFor(fullName, customer.email)}</AvatarFallback>
        </Avatar>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate font-medium">{fullName || customer.email || 'Customer'}</span>
          </TooltipTrigger>
          <TooltipContent>{fullName || customer.email || 'Customer'}</TooltipContent>
        </Tooltip>
        {linkToProfile && customer.user_id ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <LinkBase
                to={`/users/${customer.user_id}/profile`}
                className={buttonVariants({ variant: 'ghost', size: 'icon', className: 'size-6 shrink-0' })}
              >
                <ExternalLink className="size-3.5" />
              </LinkBase>
            </TooltipTrigger>
            <TooltipContent>Open full profile</TooltipContent>
          </Tooltip>
        ) : null}
        {customer.domain ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Globe className="text-muted-foreground ml-auto size-3.5 shrink-0" />
            </TooltipTrigger>
            <TooltipContent>{customer.domain}</TooltipContent>
          </Tooltip>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-2 px-4">
        <div className="flex flex-col gap-1">
          {customer.email ? (
            <span className="inline-flex items-center gap-1.5 text-sm">
              <span className="truncate">{customer.email}</span>
              <CopyIconButton value={customer.email} label="Email" />
            </span>
          ) : null}
          {customer.phone_number ? (
            <span className="inline-flex items-center gap-1.5 text-sm">
              <span className="truncate">{customer.phone_number}</span>
              <CopyIconButton value={customer.phone_number} label="Phone number" />
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {customer.company_name ? <span className="text-sm">{customer.company_name}</span> : null}
          {customer.login_type ? <Badge variant="outline">{startCase(customer.login_type)}</Badge> : null}
          {scopes.map((scope) => (
            <Badge key={scope} variant="outline">
              {scope}
            </Badge>
          ))}
        </div>
        {customer.sex || customer.date_of_birth ? (
          <span className="text-muted-foreground text-xs">
            {[customer.sex ? startCase(customer.sex) : null, customer.date_of_birth].filter(Boolean).join(' · ')}
          </span>
        ) : null}
        {signupDate ? (
          <span className="text-muted-foreground text-xs">
            {signupLabel} <DateField record={{ date: signupDate }} source="date" showTime transform={fromApiUtc} />
          </span>
        ) : null}
      </CardContent>
      {customer.email ? (
        <CardFooter className="justify-end px-4">
          <ContactsLookupButton email={customer.email} currentUserId={customer.user_id} />
        </CardFooter>
      ) : null}
    </Card>
  )
}
